/**
 * @deprecated
 */
 export function guid(): string {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
		var r = (Math.random() * 16) | 0,
			v = c == 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

/**
 * Get a random ID like `0fzzzzr726ed`
 */
export const randomId = () =>
	Math.random().toString(36).slice(2);

/**
 * Theme Helpers
 */
export const _COLORS_ = Object.freeze({
	0: 'primary',
	1: 'secondary',
	2: 'tertiary',
	4: 'quarternary',
	5: 'quinary',
	6: 'senary',
	7: 'gray-lite',
	8: 'gray-dark',
});

/**
 * Window Helpers
 */

/**
 * Breakpoints are situatued such that
 * comparison would be "and down" --
 * 
 * if (window.innerWidth < _BREAKPOINTS_.sm) { ... }
 * @deprecated
 */
export const _BREAKPOINTS_ = Object.freeze({
	xs: 576,
	sm: 767.98,
	md: 991.98,
	lg: 1199.98,
	xl: 1399.98,
	xxl: 1799.98,
	giant: Infinity
});

/**
 * ```ts
 * const tooSmall = getBreakpoint('500px').downInclusive;
 *
 * if (tooSmall.matches)
 *     console.log('@media (max-width: 500px)');
 *
 * tooSmall.addEventListener('change', ({ matches }) => {
 *     if (matches) console.log('@media (max-width: 500px)');
 * });
 * ```
 */
export const getBreakpoint = (cssLength: string) => ({
	down:          matchMedia(`(max-width:calc(${cssLength} - 0.02px))`),
	upExclusive:   matchMedia(`(min-width:calc(${cssLength} - 0.02px))`),
	downInclusive: matchMedia(`(max-width:${cssLength})`),
	up:            matchMedia(`(min-width:${cssLength})`)
}) as const;

/**
 * Enables unified media query listeners, pre-filled with breakpoints.
 * ```ts
 * if (breakpoints.lg.up.matches)
 *     console.log('@include media-breakpoint-up(lg)');
 *
 * breakpoints.lg.down.addEventListener('change', ({ matches }) => {
 *     if (matches) console.log('@include media-breakpoint-down(lg)');
 * });
 * ```
 */
export const breakpoints = {
	/** 576px */
	sm: getBreakpoint('576px'),
	/** 768px */
	md: getBreakpoint('768px'),
	/** 992px */
	lg: getBreakpoint('992px'),
	/** 1200px */
	xl: getBreakpoint('1200px'),
	/** 1400px */
	xxl: getBreakpoint('1400px'),
	/** 1800px */
	giant: getBreakpoint('1800px')
} as const;

export function getCookie(name: string) {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) return parts.pop()?.split(';').shift();
}

export const clamp = (min: number, x: number, max: number) =>
	Math.max(min, Math.min(x, max));

/** JS's `%` operator is the remainder operator. This is the real modulo function. */
export const mod = (n: number, d: number) =>
	((n % d) + d) % d;

/** Gives an array of the unique items in an iterable */
export const unique = <T>(iterable?: Iterable<T>) =>
	[...new Set(iterable)];

export const domRectCenter = (rect: DOMRectReadOnly) => ({
	x: rect.left + rect.width  / 2,
	y: rect.top  + rect.height / 2
});

export const domRectIntersection = (a: DOMRectReadOnly, b: DOMRectReadOnly) => {
	const left   = Math.max(a.left,   b.left  );
	const right  = Math.min(a.right,  b.right );
	const top    = Math.max(a.top,    b.top   );
	const bottom = Math.min(a.bottom, b.bottom);

	if (left <= right && top <= bottom)
		return new DOMRectReadOnly(
			left,
			top,
			right  - left,
			bottom - top
		);
};

export const domRectIntersectionRatio = (container: DOMRectReadOnly, target: DOMRectReadOnly) => {
	const targetArea = target.width * target.height;
	const intersection = domRectIntersection(container, target);
	if (!intersection || targetArea === 0)
		return 0;

	return (intersection.width * intersection.height) / targetArea;
};

export const distanceFromPointToPoint = (a: { x: number, y: number }, b: { x: number, y: number }) =>
	Math.hypot(
		b.x - a.x,
		b.y - a.y
	);

export const distanceFromPointToDomRect = (point: { x: number, y: number }, rect: DOMRectReadOnly) =>
	Math.hypot(
		Math.max(
			rect.left - point.x,
			0,
			point.x - rect.right
		),
		Math.max(
			rect.top - point.y,
			0,
			point.y - rect.bottom
		)
	);

export const scrollToTop = async (): Promise<void> => {
	window.scrollTo({
		behavior: 'smooth',
		top: 0
	});

	return new Promise(resolve => {
		const isDoneScrolling = () => {
			if (window.scrollY === 0) {
				resolve();
				removeEventListener('scroll', isDoneScrolling);
			}
		}
		addEventListener('scroll', isDoneScrolling, { passive: true });
		// If the window is already scrolled to the top
		isDoneScrolling();
	});
};

export const wait = <T>(ms: number, value?: T) =>
	new Promise(resolve => {
		setTimeout(() => {
			resolve(value);
		}, ms)
	});

export const deferredPromise = <Resolved = unknown, Rejected = never>() => {
	const result: {
		resolve: (x: Resolved) => void,
		reject:  (x: Rejected) => void,
		promise: Promise<Resolved | Rejected>
	} = {} as any;
	result.promise = new Promise((resolve, reject) => {
		result.resolve = resolve;
		result.reject  = reject;
	});
	return result;
};

export const debounce = <F extends (...args: Array<unknown>) => unknown>(f: F, ms = 100) => {
	let timeout: ReturnType<typeof setTimeout> | undefined;

	return (...args: Parameters<F>) => {
		if (timeout !== undefined)
			clearTimeout(timeout);

		timeout =
			setTimeout(() => {
				timeout = undefined;
				f(...args);
			}, ms);
	};
};

export const throttle = <F extends (...args: Array<unknown>) => unknown>(f: F, ms = 100) => {
	let timeout: ReturnType<typeof setTimeout> | undefined;
	let lastArgs: Parameters<F> | undefined;

	return (...args: Parameters<F>) => {
		lastArgs = args;

		if (timeout !== undefined)
			return;

		timeout =
			setTimeout(() => {
				timeout = undefined;
				f(...lastArgs!);
			}, ms);
	};
};

export const getUrlParams = () =>
	Object.fromEntries(new URL(location.href).searchParams) as Partial<Record<string, string>>;

/** Media query for when the main input method can conveniently hover */
export const hoverQuery = matchMedia('(hover: hover)');

export const pointerHover = (
	element: Element,
	options: {
		/** Only run for pointers that are truly hovering, not e.g. touching a screen. */
		onlyTrueHovers?: boolean,
		/** Runs each time a pointer `:hover`s. Return `false` to not attach any other listeners. */
		onEachEnter?(enterEvent: PointerEvent): void | false,
		/** Runs each time a pointer stops `:hover`ing. */
		onEachLeave?(leaveEvent: PointerEvent, enterEvent: PointerEvent): void,
		/** Runs when all pointers stop `:hover`ing or the signal is aborted. */
		onAllLeave?(): void,
		/** Abort all listeners */
		signal?: AbortSignal
	}
) => {
	if (options.signal?.aborted)
		return;

	const enteredPointers = new Set<number>();
	element.addEventListener('pointerenter', (enterEvent: PointerEvent) => {
		if (options.onlyTrueHovers && enterEvent.pointerType === 'touch')
			return;

		if (options.onEachEnter?.(enterEvent) === false)
			return;

		enteredPointers.add(enterEvent.pointerId);
		const onLeave = (leaveEvent: PointerEvent) => {
			if (enterEvent.pointerId !== leaveEvent.pointerId)
				return;

			options.onEachLeave?.(leaveEvent, enterEvent);
			enteredPointers.delete(leaveEvent.pointerId);
			if (!enteredPointers.size)
				options.onAllLeave?.();

			element.removeEventListener('pointerleave', onLeave);
		};
		element.addEventListener('pointerleave', onLeave, { signal: options.signal });
	}, { signal: options.signal });

	options.signal?.addEventListener('abort', () => {
		options.onAllLeave?.();
	});
};

/** Returns the first (in order) non-error response */
export const getFirstSuccessfulResponse = async (requests: ReadonlyArray<URL | RequestInfo>): Promise<Response | undefined> => {
	for (const request of requests) {
		try {
			const res = await fetch(request);
			if (res.ok)
				return res;
		}
		catch {}
	}
};

export const useNaturalImageDimensions = (img: HTMLImageElement) =>
	[img.width, img.height] = [img.naturalWidth, img.naturalHeight];

export const useNaturalVideoDimensions = (video: HTMLVideoElement) =>
	[video.width, video.height] = [video.videoWidth, video.videoHeight];

export const useNaturalDimensions = (e: HTMLImageElement | HTMLVideoElement) => {
	if (e instanceof HTMLImageElement)
		useNaturalImageDimensions(e);
	if (e instanceof HTMLVideoElement)
		useNaturalVideoDimensions(e);
};

export const imgRefSetDimensions = async (img?: HTMLImageElement): Promise<[number, number] | undefined> => {
	if (!img)
		return;

	if (img.complete)
		return useNaturalImageDimensions(img);

	return new Promise(resolve => {
		img.addEventListener('load', () => {
			resolve(useNaturalImageDimensions(img));
		}, { once: true });
	});
};

export const getPreferredLocales = (element?: Element) => {
	let override =
		element instanceof HTMLElement && element.lang
			? [element.lang]
			: [];

	const htmlLangs: Array<string> = [];
	let currentElement = element?.closest('[lang]');
	while (true) {
		if (!(currentElement instanceof HTMLElement))
			break;

		try {
			const locale = new Intl.Locale(currentElement.lang);
			htmlLangs.push(locale.toString());
		}
		catch {
			break;
		}

		const previousElement = currentElement;
		currentElement = previousElement.parentElement?.closest('[lang]');
		if (currentElement == previousElement)
			break;
	}

	return unique([
		...override,
		...navigator.languages,
		...htmlLangs
	]);
};

export const toISOStringLocal = (date) => {
	const pad = (n) => (n < 10 ? "0" + n : n);

	return (
		date.getFullYear() +
		"-" +
		pad(date.getMonth() + 1) +
		"-" +
		pad(date.getDate()) +
		"T" +
		pad(date.getHours()) +
		":" +
		pad(date.getMinutes()) +
		":" +
		pad(date.getSeconds())
	);
};
